import { Camera, ImagePlus, PenLine, Receipt } from "lucide-react";
import {
  type ChangeEvent,
  createContext,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { useAssistantEntry } from "@/features/assistant/AssistantProvider";
import { CAMERA_ACCEPT, GALLERY_ACCEPT } from "@/features/assistant/photo";
import { currentBreakpoint } from "@/lib/breakpoint";
import { useOnline } from "@/pwa/online";
import { useOpenQuickAdd } from "./QuickAdd";
import { useCan } from "./workspace-context";

const NEEDS_NETWORK = "O assistente precisa de conexão com a internet.";
const PHOTO_HINT = "Nota, PIX, boleto, recibo…";

interface NewEntry {
  /** Abre a escolha (ou, sem assistente, direto o formulário). Recebe o clique para saber quem abriu. */
  open(event?: { currentTarget: HTMLElement }): void;
  /** Atributos do botão que abre: anunciam o diálogo só quando a escolha existe. */
  triggerProps: { "aria-haspopup"?: "dialog"; "aria-expanded"?: boolean };
}

const NewEntryContext = createContext<NewEntry | null>(null);

/** O "+" do shell e o "Novo lançamento" do topo. */
export function useOpenNewEntry() {
  const entry = useContext(NewEntryContext);
  if (!entry) throw new Error("useOpenNewEntry fora do NewEntryProvider");
  return entry;
}

/**
 * "Novo lançamento": com o assistente liberado e papel que lança, pergunta como lançar (digitar ou foto do
 * comprovante); sem isso, abre direto o lançamento rápido, como antes. Os inputs de arquivo ficam aqui, fora da
 * escolha: ela fecha no mesmo gesto que abre a câmera, e um input desmontado perderia o `change`.
 */
export function NewEntryProvider({ children }: { children: ReactNode }) {
  const assistant = useAssistantEntry();
  const canWrite = useCan("transaction:write");
  const openQuickAdd = useOpenQuickAdd();
  const hasChoice = assistant !== null && canWrite;
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState(false);
  const opener = useRef<HTMLElement | null>(null);
  // Fechar por Esc, toque fora ou "Fechar" devolve o foco a quem abriu; "Digitar" e a foto cuidam disso antes.
  const restoreFocus = useRef(true);
  const camera = useRef<HTMLInputElement>(null);
  const gallery = useRef<HTMLInputElement>(null);

  const openEntry = useCallback(
    (event?: { currentTarget: HTMLElement }) => {
      if (!hasChoice) {
        openQuickAdd();
        return;
      }
      opener.current = event?.currentTarget ?? (document.activeElement as HTMLElement | null);
      restoreFocus.current = true;
      setMobile(currentBreakpoint() === "mobile");
      setOpen(true);
    },
    [hasChoice, openQuickAdd],
  );

  // Fecha já (sem esperar a animação) e põe o foco em quem abriu, para o próximo painel saber a quem devolvê-lo.
  function closeToOpener() {
    restoreFocus.current = false;
    flushSync(() => setOpen(false));
    opener.current?.focus();
  }

  function type() {
    closeToOpener();
    openQuickAdd();
  }

  function pick(input: RefObject<HTMLInputElement | null>) {
    // O seletor abre no mesmo gesto do toque (o navegador exige); a escolha fecha logo depois.
    input.current?.click();
    closeToOpener();
  }

  function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    // Seletor fechado sem imagem: nada acontece.
    if (file) assistant?.openWithPhoto(file, opener.current);
  }

  const value = useMemo<NewEntry>(
    () => ({
      open: openEntry,
      triggerProps: hasChoice ? { "aria-haspopup": "dialog", "aria-expanded": open } : {},
    }),
    [openEntry, hasChoice, open],
  );

  // Ao abrir, o foco vai para a primeira opção (o drawer, sozinho, não leva o foco a ela).
  const onOpenAutoFocus = (e: Event) => {
    e.preventDefault();
    (e.currentTarget as HTMLElement | null)?.querySelector<HTMLElement>("[data-first-option]")?.focus();
  };
  const onCloseAutoFocus = (e: Event) => {
    e.preventDefault();
    if (restoreFocus.current) opener.current?.focus();
  };
  const body = open && (
    <Choice mobile={mobile} onType={type} onCamera={() => pick(camera)} onGallery={() => pick(gallery)} />
  );

  return (
    <NewEntryContext.Provider value={value}>
      {children}
      {hasChoice && (
        <>
          <input
            ref={camera}
            type="file"
            accept={CAMERA_ACCEPT}
            capture="environment"
            hidden
            tabIndex={-1}
            onChange={onFile}
          />
          <input ref={gallery} type="file" accept={GALLERY_ACCEPT} hidden tabIndex={-1} onChange={onFile} />
          {mobile ? (
            <Drawer open={open} onOpenChange={setOpen}>
              <DrawerContent
                aria-describedby={undefined}
                onOpenAutoFocus={onOpenAutoFocus}
                onCloseAutoFocus={onCloseAutoFocus}
              >
                <div className="flex flex-col gap-4 px-4 pb-6">
                  <DrawerTitle>Novo lançamento</DrawerTitle>
                  {body}
                </div>
              </DrawerContent>
            </Drawer>
          ) : (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogContent
                aria-describedby={undefined}
                onOpenAutoFocus={onOpenAutoFocus}
                onCloseAutoFocus={onCloseAutoFocus}
                className="max-w-sm"
              >
                <div className="flex flex-col gap-4 p-6">
                  <DialogTitle>Novo lançamento</DialogTitle>
                  {body}
                </div>
              </DialogContent>
            </Dialog>
          )}
        </>
      )}
    </NewEntryContext.Provider>
  );
}

/** As opções (D1): no celular, câmera e galeria separadas; no tablet e no desktop, o seletor de arquivos. */
function Choice({
  mobile,
  onType,
  onCamera,
  onGallery,
}: {
  mobile: boolean;
  onType: () => void;
  onCamera: () => void;
  onGallery: () => void;
}) {
  const online = useOnline();
  const [tried, setTried] = useState(false);
  // Sem rede, a foto nem abre o seletor: o assistente não funcionaria (o formulário, sim).
  const photo = (go: () => void) => () => {
    if (!online) {
      setTried(true);
      return;
    }
    go();
  };
  return (
    <div className="flex flex-col gap-2">
      {tried && !online && (
        <p role="alert" className="m-0 rounded-control bg-expense-soft px-3 py-2 text-sm text-expense">
          {NEEDS_NETWORK}
        </p>
      )}
      <Option icon={PenLine} label="Digitar" hint="Valor, conta e categoria" onClick={onType} first />
      {mobile ? (
        <>
          <Option icon={Camera} label="Tirar foto" hint={PHOTO_HINT} onClick={photo(onCamera)} />
          <Option
            icon={ImagePlus}
            label="Escolher imagem"
            hint="Print ou foto da galeria"
            onClick={photo(onGallery)}
          />
        </>
      ) : (
        <Option icon={Receipt} label="Foto do comprovante" hint={PHOTO_HINT} onClick={photo(onGallery)} />
      )}
    </div>
  );
}

function Option({
  icon: Icon,
  label,
  hint,
  onClick,
  first = false,
}: {
  icon: typeof Camera;
  label: string;
  hint: string;
  onClick: () => void;
  first?: boolean;
}) {
  const hintId = useId();
  return (
    <button
      type="button"
      // O nome é só o rótulo; a linha de apoio é a descrição.
      aria-label={label}
      aria-describedby={hintId}
      data-first-option={first || undefined}
      onClick={onClick}
      className="flex min-h-14 w-full items-center gap-3 rounded-card bg-surface-2 px-4 py-3 text-left text-ink hover:bg-accent-soft focus-visible:outline-2 focus-visible:outline-accent"
    >
      <Icon aria-hidden="true" className="size-6 shrink-0 text-accent-soft-ink" />
      <span className="flex min-w-0 flex-col">
        <span className="text-[15px] font-semibold">{label}</span>
        <span id={hintId} className="text-sm text-muted">
          {hint}
        </span>
      </span>
    </button>
  );
}
